import {
  Autocomplete,
  AutocompleteItem,
  Avatar,
  Button,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  useDisclosure,
} from '@heroui/react';
import { Brand } from '@ui/brand';
import { Location, SearchNormal1, Shop, Heart, Profile } from 'iconsax-react';
import { useSetAtom, useAtomValue } from 'jotai';
import { Icon } from '@iconify/react';
import { NavbarCategories } from '@features/app/navbar-categories';
import { useState } from 'react';
import { useUser } from '../../entites/authorization/use-user.ts';
import { loginModalAtom } from '@app/atoms/app/app.atom.ts';
import { useProducts } from '../../entites/product';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { cityAtom } from '@app/atoms/app/city.atom.ts';
import { SearchLocation } from '@features/location/search-location';
import { Locations } from '@features/location/locations';

const NavbarLocationModal = () => {
  const selectedCity = useAtomValue(cityAtom);
  const [query, setQuery] = useState('');
  const modal = useDisclosure();

  return (
    <>
      <Button
        size="sm"
        variant="light"
        className="text-[#60656C]"
        startContent={
          <Avatar
            className="bg-[#F5F5F5]"
            showFallback
            fallback={<Location size="16" color="#006FEE" />}
            radius="md"
            size="sm"
          />
        }
        onPress={modal.onOpen}
      >
        {selectedCity.name}
      </Button>

      <Modal onOpenChange={modal.onOpenChange} size="3xl" isOpen={modal.isOpen}>
        <ModalContent>
          <ModalHeader className="">Выберите город</ModalHeader>
          <ModalBody className="pb-6">
            <SearchLocation onSearch={setQuery} />
            <Locations
              className="grid grid-cols-2 gap-x-6 gap-y-2"
              query={query}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export const DesktopNavbar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const setIsOpen = useSetAtom(loginModalAtom);
  const [query, setQuery] = useState('');
  const { isAuthenticated } = useUser();

  const { rawData, isLoading } = useProducts({ query });

  return (
    <>
      <div className="h-11 sticky w-full bg-[#F3F8FE] z-40 inset-x-0">
        <div className="container max-w-7xl mx-auto flex flex-row justify-between items-center h-full">
          <NavbarLocationModal />

          <div className="flex flex-row items-center gap-x-10">
            <Link href="/contacts" className="text-[#60656C] text-sm" size="sm">
              Служба поддержки
            </Link>
          </div>
        </div>
      </div>
      <Navbar
        isBordered
        isBlurred={false}
        maxWidth="xl"
        classNames={{ wrapper: 'px-0 pr-4', base: 'h-[92px]' }}
      >
        <NavbarBrand className="flex-grow-0">
          <Link
            href="/"
            color="foreground"
            className="flex"
            aria-current="page"
          >
            <div className="flex items-center justify-center h-12 w-[120px] overflow-hidden">
              <Brand />
            </div>
          </Link>
        </NavbarBrand>

        <NavbarContent className="w-full sm:flex gap-4" justify="center">
          <NavbarItem className="flex flex-row gap-x-1">
            <Button
              size="lg"
              color="primary"
              className="w-[220px]"
              startContent={
                <Icon
                  width={20}
                  height={20}
                  icon={
                    searchParams.has('menu')
                      ? 'material-symbols:close-rounded'
                      : 'solar:widget-6-linear'
                  }
                />
              }
              onPress={() => {
                if (searchParams.has('menu')) {
                  searchParams.delete('menu');
                } else {
                  searchParams.set('menu', 'open');
                }
                setSearchParams(searchParams);
              }}
            >
              Каталог
            </Button>
          </NavbarItem>

          <NavbarItem className="flex flex-row w-[520px]">
            <Input
              isClearable
              onValueChange={setQuery}
              value={query}
              className="hidden"
              onClear={() => {
                setQuery('');
                searchParams.delete('query');
                setSearchParams(searchParams);
              }}
              onFocus={() => {
                if (pathname !== '/search') navigate('/search');
              }}
              startContent={<SearchNormal1 size="20" color="#9CA5AC" />}
              classNames={{
                inputWrapper: 'h-12 rounded-r-none',
              }}
            />

            <Autocomplete
              placeholder="Искать в zerde"
              aria-label="Искать в zerde"
              isClearable
              isLoading={isLoading}
              // className="hidden"
              listboxProps={{
                emptyContent: 'Введите название товара',
              }}
              onFocus={() => {
                if (pathname !== '/search') navigate('/search');
              }}
              items={
                (query.length > 0 &&
                  rawData?.map((item) => ({
                    key: item.name,
                    label: item.name,
                  }))) ||
                []
              }
              // inputValue={query}
              onSelectionChange={(item) => {
                if (item && typeof item === 'string') {
                  searchParams.set('query', item);
                  setSearchParams(searchParams);
                  // const params = new URLSearchParams({
                  //   query: item,
                  // }).toString();
                  // navigate(`/search?${params}`);
                  // navigate(`/products/${item}`);
                }
              }}
              onInputChange={setQuery}
              startContent={<SearchNormal1 size="20" color="#9CA5AC" />}
              inputProps={{
                classNames: {
                  inputWrapper: 'rounded-r-none h-12',
                },
              }}
            >
              {(item) => (
                <AutocompleteItem key={item.key}>{item.label}</AutocompleteItem>
              )}
            </Autocomplete>
            <Button
              size="lg"
              color="primary"
              className="rounded-l-none"
              onPress={() => {
                searchParams.set('query', query);
                setSearchParams(searchParams);
              }}
            >
              Найти
            </Button>
          </NavbarItem>
        </NavbarContent>

        <NavbarContent justify="end">
          {isAuthenticated && (
            <NavbarItem>
              <Button
                as={Link}
                href="/favorite"
                radius="sm"
                variant="light"
                className="text-sm text-[#222222] max-w-none flex flex-col items-center justify-center gap-y-2 h-13 w-[76px] min-w-0 px-0"
              >
                <Heart size="24" color="#222222" />
                Избранное
              </Button>
            </NavbarItem>
          )}

          <NavbarItem>
            <Button
              as={Link}
              href="/cart"
              variant="light"
              className="text-sm text-[#222222] flex flex-col items-center justify-center gap-y-2 h-13 w-[68px] min-w-0 px-0"
            >
              <Icon
                width={24}
                height={24}
                icon="solar:bag-4-outline"
                color="#222222"
              />
              Корзина
            </Button>
          </NavbarItem>

          <NavbarItem>
            <Button
              as={Link}
              href="/store"
              variant="light"
              className="text-sm text-[#222222] flex flex-col items-center justify-center gap-y-2 h-13 w-[68px] min-w-0 px-0"
            >
              <Shop size="24" color="#222222" />
              Аптеки
            </Button>
          </NavbarItem>

          {!isAuthenticated && (
            <NavbarItem>
              <Button
                variant="light"
                className="text-sm text-[#222222] flex flex-col items-center justify-center gap-y-2 h-13 w-[68px] min-w-0 px-0"
                onPress={() => setIsOpen(true)}
              >
                <Icon
                  width={24}
                  height={24}
                  icon="solar:login-3-outline"
                  color="#222222"
                />
                Войти
              </Button>
            </NavbarItem>
          )}

          {isAuthenticated && (
            <NavbarItem>
              <Button
                as={Link}
                href="/profile"
                variant="light"
                className="text-sm text-[#222222] flex flex-col items-center justify-center gap-y-2 h-13 w-[68px] min-w-0 px-0"
              >
                <Profile size="24" color="#222222" />
                Профиль
              </Button>
            </NavbarItem>
          )}
        </NavbarContent>
      </Navbar>

      <NavbarCategories isShow={searchParams.has('menu')} />
    </>
  );
};
